import { Injectable } from "@angular/core"
import { FireStoreService } from "@shared/service/fire-store.service"
import { REFERENTIAL, ReferentialDTO, ReferentialService } from "@app/pages/referential/referential.service"
import { PersonService } from "@app/pages/person/person.service"

@Injectable({
  providedIn: "root",
})
export class PartService extends FireStoreService<PartDTO> {
  constructor(private refService: ReferentialService, private personService: PersonService) {
    super()
    this.init({
      collectionKey: "parts",
      fullTextFields: ["name"],
      sorting: {
        field: "name",
      },
    })
  }

  toDTO(dto: any): Promise<PartDTO> {
    return Promise.all([
      this.refService.getAllFromCache(REFERENTIAL.unity_type),
      this.refService.getAllFromCache(REFERENTIAL.unit_tags),
      this.personService.getAllFromCache().catch(() => []),
    ]).then(([refTypes, refTags, persons]) => {
      const ref = refTypes.find((x: ReferentialDTO) => x.id === dto.type?.id)
      dto.type_label = ref?.name || "?"
      dto.type_color = ref?.color || "?"
      dto.choreographer_label = persons.find((x) => x.id === dto.choreographer?.id)?.fullName || ""
      dto.tags_label = (dto.tags || [])
        .map((t: { id: string }) => refTags.find((x: ReferentialDTO) => x.id === t.id)?.name || "?")
        .join(", ")
      const version =
        (dto.versions || []).find((v: PartVersionDTO) => v.id === dto.versionIdDefault) ||
        dto.versions?.[dto.versions.length - 1]
      dto.versions_count = dto.versions?.length || 0
      dto.duration_default = version?.duration || ""
      dto.places_count = version?.places?.length || 0
      return dto
    })
  }

  newPlacePoint(): PlacePointDTO {
    return { id: this.uuid(), name: "", points: 0 }
  }

  // une place est créée avec une première version de points, qui est sa version par défaut
  newPlace(): PlaceDTO {
    const point = this.newPlacePoint()
    return { id: this.uuid(), name: "", points: [point], pointIdDefault: point.id }
  }

  // la place créée avec la version est ajoutée aux places de la partie
  newPartVersion(part: PartDTO): PartVersionDTO {
    const place = this.newPlace()
    part.places = part.places || []
    part.places.push(place)
    return { id: this.uuid(), name: "", duration: "", places: [{ id: place.id }] }
  }

  // garantit des id et une valeur par défaut valide à chaque niveau :
  // versionIdDefault (partie), pointIdDefault (place).
  // Si la valeur par défaut est absente ou ne pointe plus sur un élément existant => premier élément.
  // Les références de version vers des places absentes de la partie sont retirées,
  // ainsi que les places qui ne sont plus utilisées par aucune version
  ensureDefaults(part: PartDTO): PartDTO {
    const withId = <T extends { id: string }>(list?: Array<T>): Array<T> => {
      list?.forEach((x) => (x.id = x.id || this.uuid()))
      return list || []
    }
    const defaultId = (list: Array<{ id: string }>, currentId?: string): string =>
      list.find((x) => x.id === currentId)?.id || list[0]?.id || ""

    const places = withId(part.places)
    places.forEach((place) => {
      const points = withId(Array.isArray(place.points) ? place.points : undefined)
      place.pointIdDefault = defaultId(points, place.pointIdDefault)
    })
    const placeIds = new Set(places.map((p) => p.id))
    const versions = withId(part.versions)
    versions.forEach((version) => (version.places = (version.places || []).filter((p) => placeIds.has(p.id))))
    const usedIds = new Set<string>()
    versions.forEach((v) => v.places.forEach((p) => usedIds.add(p.id)))
    part.places = places.filter((p) => usedIds.has(p.id))
    part.versionIdDefault = defaultId(versions, part.versionIdDefault)
    return part
  }
}

// places d'une version, dans l'ordre de la version
export function partVersionPlaces(part?: PartDTO, version?: PartVersionDTO): Array<PlaceDTO> {
  return (version?.places || [])
    .map((ref) => part?.places?.find((p) => p.id === ref.id))
    .filter((p): p is PlaceDTO => !!p)
}

// [Pièce-mère] - [Scène] - [Nom], sans séparateur pour les champs vides
export function partTitle(part?: Partial<Pick<PartDTO, "parentUnit" | "scene" | "name">>): string {
  return [part?.parentUnit, part?.scene, part?.name]
    .map((x) => x?.trim())
    .filter(Boolean)
    .join(" - ")
}

export interface PartDTO {
  id: string
  name: string
  subtitle?: string
  parentUnit?: string
  scene?: string
  choreographer?: { id: string /*personId*/ }
  type?: { id: string /*REFERENTIAL.unity_type*/ }
  tags?: Array<{ id: string /*REFERENTIAL.unit_tags*/ }>

  places: Array<PlaceDTO>
  versions: Array<PartVersionDTO>
  versionIdDefault: string // id of PartVersionDTO loaded by default

  type_label?: string
  type_color?: string
  tags_label?: string
  choreographer_label?: string
  versions_count?: number
  duration_default?: string
  places_count?: number
}

export interface PartVersionDTO {
  id: string
  duration: string
  name: string

  places: Array<{ id: string /*PlaceDTO de la partie*/ }>
  refPerformanceId?: string
}

export interface PlaceDTO {
  id: string
  name: string
  points: Array<PlacePointDTO>
  pointIdDefault: string // id of PlacePointDTO loaded by default
  group?: boolean
  roleIds?: Array<string> // ids of RoleDTO (facultatif, une place peut porter plusieurs rôles)
  note?: string // facultatif
  gender?: PlaceGender
  noDance?: boolean
}

export type PlaceGender = "F" | "M" | "X"

export const PLACE_GENDER_OPTIONS: Array<{ value: PlaceGender; symbol: string; label: string }> = [
  { value: "F", symbol: "♀", label: "Femme" },
  { value: "M", symbol: "♂", label: "Homme" },
  { value: "X", symbol: "⚥", label: "Mixte" },
]

export function placeGenderSymbol(place?: Pick<PlaceDTO, "gender">): string {
  return PLACE_GENDER_OPTIONS.find((o) => o.value === place?.gender)?.symbol || ""
}

// place non dansée : nom entre accolades
export function placeName(place?: Pick<PlaceDTO, "name" | "noDance">): string {
  const name = place?.name || "?"
  return place?.noDance ? "{" + name + "}" : name
}

export interface PlacePointDTO {
  id: string
  points: number
  name: string

  refPerformanceId?: string
}
